import { Flex, Icon, Input, Separator, Spinner, Text } from '@chakra-ui/react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Check, Search } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useGetPbxList, useGetPbxListInfinite } from '~/queries/pbxQueries'
import { getRecentPbxs } from '~/utils/recentPbxs'

import { PaginatedApiResult, Pbx } from '@mangovoice/types'

type PbxSummary = Pick<Pbx, 'id' | 'name'>

const RECENTS_THRESHOLD = 15
const MAX_RECENTS = 5

const COMBOBOX_AUTOCOMPLETE = 'list'

const OptionSection = {
  Recent: 'recent',
  Main: 'main',
} as const
type OptionSection = (typeof OptionSection)[keyof typeof OptionSection]
type GetOptionId = (section: OptionSection, pbxId: number) => string

interface PbxPickerProps {
  currentPbx?: PbxSummary
  currentPbxId?: number
  onSelect: (pbx: PbxSummary) => void
  initialPbxList?: PaginatedApiResult<Pbx>
  isLoading?: boolean
  /** Id of a visible heading that names the PBX listbox. */
  listLabelledBy?: string
}

function useDebounce(value: string, delay: number) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(id)
  }, [value, delay])
  return debounced
}

export const PbxPicker = ({
  currentPbx,
  currentPbxId,
  onSelect,
  initialPbxList,
  isLoading: externalLoading,
  listLabelledBy,
}: PbxPickerProps) => {
  const resolvedCurrentPbxId = currentPbx?.id ?? currentPbxId
  const { t } = useTranslation()
  const baseId = useId()
  const listId = `${baseId}-list`
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)

  // Initial fetch to get total count (small page, just need count)
  const { data: countData, isLoading: countLoading } = useGetPbxList({
    apiParams: { limit: 1 },
    options: { enabled: !initialPbxList },
  })

  const totalCount = initialPbxList?.count ?? countData?.count ?? 0
  const isSmallList = totalCount <= RECENTS_THRESHOLD
  const showRecents = totalCount > RECENTS_THRESHOLD
  const loading = externalLoading ?? countLoading

  // For small lists, fetch everything in one shot
  const { data: smallListData } = useGetPbxList({
    apiParams: { limit: totalCount },
    options: { enabled: !initialPbxList && isSmallList && totalCount > 0 },
  })

  const smallPbxs = initialPbxList?.results ?? smallListData?.results ?? []

  // For large lists, use infinite query with server-side search
  const isSearching = !!debouncedSearch
  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: infiniteLoading,
  } = useGetPbxListInfinite({
    search: isSearching ? debouncedSearch : undefined,
    enabled: !isSmallList && totalCount > 0,
  })

  const infinitePbxs = useMemo(
    () => infiniteData?.pages.flatMap((page) => page.results) ?? [],
    [infiniteData],
  )
  const infiniteTotalCount = infiniteData?.pages[0]?.count ?? totalCount

  // Client-side filtering for small lists
  const filteredSmallList = useMemo(() => {
    if (!isSmallList) return []
    if (!search.trim()) return smallPbxs
    const q = search.toLowerCase()
    return smallPbxs.filter((pbx) => pbx.name.toLowerCase().includes(q))
  }, [isSmallList, smallPbxs, search])

  // Recents
  const recentPbxs: PbxSummary[] = useMemo(() => {
    if (!showRecents) return []
    return getRecentPbxs()
      .slice(0, MAX_RECENTS)
      .map((r) => ({ id: r.id, name: r.name }))
  }, [showRecents])

  const filteredRecents = useMemo(() => {
    if (!showRecents) return []
    const filtered = resolvedCurrentPbxId
      ? recentPbxs.filter((r) => r.id !== resolvedCurrentPbxId)
      : recentPbxs
    return filtered
  }, [showRecents, recentPbxs, resolvedCurrentPbxId])

  // All PBXs for the main list (deduped against current PBX)
  const mainListPbxs = useMemo(() => {
    if (isSmallList) return filteredSmallList
    return infinitePbxs
  }, [isSmallList, filteredSmallList, infinitePbxs])

  const recentsListed =
    showRecents && filteredRecents.length > 0 && !isSearching

  // Flat selectable items for keyboard navigation
  const selectableItems = useMemo<PbxSummary[]>(() => {
    const items: PbxSummary[] = []
    if (recentsListed) {
      items.push(...filteredRecents)
    }
    items.push(...mainListPbxs)
    return items
  }, [recentsListed, filteredRecents, mainListPbxs])

  const getOptionId = useCallback<GetOptionId>(
    (section, pbxId) => `${baseId}-${section}-${pbxId}`,
    [baseId],
  )

  // Keyboard navigation
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  // State rather than a ref: the virtualizer reads it during its own layout
  // effect, which runs before this parent's ref would be attached.
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null,
  )
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // The highlight is only drawn once the user navigates with the keyboard, so
  // starting it on the current PBX doesn't change what mouse users see.
  const [isKeyboardNavigating, setIsKeyboardNavigating] = useState(false)

  // Virtualized rows may not be rendered, so only the small list starts the
  // highlight on the current PBX.
  useEffect(() => {
    setHighlightedIndex(
      isSmallList
        ? selectableItems.findIndex((pbx) => pbx.id === resolvedCurrentPbxId)
        : -1,
    )
  }, [selectableItems, isSmallList, resolvedCurrentPbxId])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (selectableItems.length === 0) return

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault()
          setIsKeyboardNavigating(true)
          setHighlightedIndex((prev) =>
            prev < selectableItems.length - 1 ? prev + 1 : 0,
          )
          break
        }
        case 'ArrowUp': {
          e.preventDefault()
          setIsKeyboardNavigating(true)
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : selectableItems.length - 1,
          )
          break
        }
        case 'Home':
        case 'End': {
          // In the search box, Home/End keep moving the text cursor once the
          // user has typed and isn't navigating the options.
          if (!isSmallList && search !== '' && highlightedIndex < 0) break
          e.preventDefault()
          setIsKeyboardNavigating(true)
          setHighlightedIndex(e.key === 'Home' ? 0 : selectableItems.length - 1)
          break
        }
        case 'Enter': {
          if (
            highlightedIndex >= 0 &&
            highlightedIndex < selectableItems.length
          ) {
            e.preventDefault()
            onSelect(selectableItems[highlightedIndex])
          }
          break
        }
      }
    },
    [selectableItems, highlightedIndex, onSelect, isSmallList, search],
  )

  const highlightedPbx = selectableItems[highlightedIndex]
  const activeOptionId = highlightedPbx
    ? getOptionId(
        recentsListed && highlightedIndex < filteredRecents.length
          ? OptionSection.Recent
          : OptionSection.Main,
        highlightedPbx.id,
      )
    : undefined

  const highlightedOptionId = isKeyboardNavigating ? activeOptionId : undefined

  // Display count
  const displayCount = isSearching ? infiniteTotalCount : totalCount

  const showSearch = !isSmallList

  useEffect(() => {
    if (isSmallList) {
      scrollElement?.focus()
    }
  }, [isSmallList, scrollElement])

  if (loading) {
    return (
      <Flex align="center" justify="center" py="8">
        <Spinner size="md" color="fg.subtle" />
      </Flex>
    )
  }

  const listName = listLabelledBy
    ? { 'aria-labelledby': listLabelledBy }
    : { 'aria-label': t('pbxPicker.allPbxs', 'All PBXs') }

  return (
    <Flex direction="column" gap="2">
      {/* Search */}
      {showSearch && (
        <Flex
          align="center"
          gap="2"
          px="3"
          py="2"
          borderWidth="1px"
          borderColor="border.emphasized"
          borderRadius="md"
          bg="bg.panel"
        >
          <Icon as={Search} boxSize="4" color="fg.subtle" aria-hidden />
          <Input
            ref={inputRef}
            role="combobox"
            aria-label={t('pbxPicker.searchLabel', 'Search PBXs')}
            aria-autocomplete={COMBOBOX_AUTOCOMPLETE}
            aria-expanded
            aria-controls={listId}
            aria-activedescendant={activeOptionId}
            variant="flushed"
            placeholder={t('pbxPicker.searchPlaceholder', 'Search PBXs...')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            size="sm"
            borderBottom="none"
            _focus={{ borderBottom: 'none', boxShadow: 'none' }}
          />
        </Flex>
      )}

      {/* Count indicator */}
      {totalCount > 0 && (
        <Text fontSize="xs" color="fg.subtle" px="3">
          {isSearching
            ? t('pbxPicker.countFiltered', '{{count}} PBXs found', {
                count: displayCount,
              })
            : t('pbxPicker.count', '{{count}} PBXs', { count: displayCount })}
        </Text>
      )}

      {/* List */}
      <Flex
        direction="column"
        maxH="20rem"
        overflowY="auto"
        ref={setScrollElement}
        gap="0.5"
        role="listbox"
        id={listId}
        {...listName}
        {...(isSmallList && {
          tabIndex: 0,
          outline: 'none',
          _focusVisible: {
            outline: '2px solid',
            outlineColor: 'brand.fg',
            outlineOffset: '-2px',
            '& [data-active]': {
              outline: '1px solid',
              outlineColor: 'border.emphasized',
              outlineOffset: '-1px',
            },
          },
          onKeyDown: handleKeyDown,
          'aria-activedescendant': activeOptionId,
        })}
      >
        {isSmallList ? (
          <SmallListView
            pbxs={filteredSmallList}
            currentPbxId={resolvedCurrentPbxId}
            onSelect={onSelect}
            noResultsText={t('pbxPicker.noResults', 'No PBXs found')}
            activeOptionId={activeOptionId}
            highlightedOptionId={highlightedOptionId}
            getOptionId={getOptionId}
            scrollElement={scrollElement}
          />
        ) : (
          <LargeListView
            recentPbxs={filteredRecents}
            mainPbxs={mainListPbxs}
            isSearching={isSearching}
            searchLoading={infiniteLoading && isSearching}
            currentPbxId={resolvedCurrentPbxId}
            onSelect={onSelect}
            recentLabel={t('pbxPicker.recent', 'Recent')}
            allPbxsLabel={
              isSearching
                ? t('pbxPicker.searchResults', 'Search Results')
                : t('pbxPicker.allPbxs', 'All PBXs')
            }
            noResultsText={t('pbxPicker.noResults', 'No PBXs found')}
            highlightedOptionId={highlightedOptionId}
            getOptionId={getOptionId}
            scrollElement={scrollElement}
            hasNextPage={!!hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
          />
        )}
      </Flex>
    </Flex>
  )
}

function SectionHeader({ id, label }: { id: string; label: string }) {
  return (
    <Text
      id={id}
      fontSize="xs"
      fontWeight="600"
      color="fg.subtle"
      px="3"
      py="1"
      textTransform="uppercase"
      letterSpacing="wider"
      aria-hidden
    >
      {label}
    </Text>
  )
}

function PbxListItem({
  pbx,
  optionId,
  isCurrent,
  isActive,
  isHighlighted,
  onSelect,
  scrollElement,
}: {
  pbx: PbxSummary
  optionId: string
  isCurrent: boolean
  isActive?: boolean
  isHighlighted?: boolean
  onSelect: (pbx: PbxSummary) => void
  scrollElement?: HTMLDivElement | null
}) {
  const itemRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isHighlighted && itemRef.current && scrollElement) {
      const item = itemRef.current
      const container = scrollElement
      const itemTop = item.offsetTop
      const itemBottom = itemTop + item.offsetHeight
      const scrollTop = container.scrollTop
      const containerBottom = scrollTop + container.clientHeight

      if (itemTop < scrollTop) {
        container.scrollTop = itemTop
      } else if (itemBottom > containerBottom) {
        container.scrollTop = itemBottom - container.clientHeight
      }
    }
  }, [isHighlighted, scrollElement])

  return (
    <Flex
      ref={itemRef}
      id={optionId}
      role="option"
      aria-selected={isCurrent}
      aria-current={isCurrent || undefined}
      align="center"
      justify="space-between"
      px="3"
      py="2"
      borderRadius="md"
      cursor="pointer"
      bg={
        isCurrent ? 'bg.emphasized' : isHighlighted ? 'bg.muted' : 'transparent'
      }
      outline={isHighlighted ? '1px solid' : 'none'}
      outlineColor={isHighlighted ? 'border.emphasized' : undefined}
      outlineOffset="-1px"
      _hover={{ backgroundColor: 'bg.emphasized' }}
      onClick={() => onSelect(pbx)}
      data-highlighted={isHighlighted || undefined}
      data-active={isActive || undefined}
    >
      <Text
        fontSize="sm"
        fontWeight={isCurrent ? '600' : '400'}
        color="fg.default"
      >
        {pbx.name}
      </Text>
      {isCurrent && (
        <Icon as={Check} boxSize="4" color="fg.subtle" aria-hidden />
      )}
    </Flex>
  )
}

function SmallListView({
  pbxs,
  currentPbxId,
  onSelect,
  noResultsText,
  activeOptionId,
  highlightedOptionId,
  getOptionId,
  scrollElement,
}: {
  pbxs: PbxSummary[]
  currentPbxId?: number
  onSelect: (pbx: PbxSummary) => void
  noResultsText: string
  activeOptionId?: string
  highlightedOptionId?: string
  getOptionId: GetOptionId
  scrollElement: HTMLDivElement | null
}) {
  return (
    <>
      {pbxs.length === 0 ? (
        <Text fontSize="sm" color="fg.subtle" px="3" py="4" textAlign="center">
          {noResultsText}
        </Text>
      ) : (
        pbxs.map((pbx) => {
          const optionId = getOptionId(OptionSection.Main, pbx.id)
          return (
            <PbxListItem
              key={pbx.id}
              pbx={pbx}
              optionId={optionId}
              isCurrent={pbx.id === currentPbxId}
              isActive={optionId === activeOptionId}
              isHighlighted={optionId === highlightedOptionId}
              onSelect={() => onSelect(pbx)}
              scrollElement={scrollElement}
            />
          )
        })
      )}
    </>
  )
}

const ROW_HEIGHT = 40

function LargeListView({
  recentPbxs,
  mainPbxs,
  isSearching,
  searchLoading,
  currentPbxId,
  onSelect,
  recentLabel,
  allPbxsLabel,
  noResultsText,
  highlightedOptionId,
  getOptionId,
  scrollElement,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  recentPbxs: PbxSummary[]
  mainPbxs: PbxSummary[]
  isSearching: boolean
  searchLoading: boolean
  currentPbxId?: number
  onSelect: (pbx: PbxSummary) => void
  recentLabel: string
  allPbxsLabel: string
  noResultsText: string
  highlightedOptionId?: string
  getOptionId: GetOptionId
  scrollElement: HTMLDivElement | null
  hasNextPage: boolean
  isFetchingNextPage: boolean
  onLoadMore: () => void
}) {
  const showRecents = recentPbxs.length > 0 && !isSearching
  const showRows = !searchLoading && mainPbxs.length > 0

  const rowsContainerRef = useRef<HTMLDivElement>(null)
  const recentHeaderId = useId()
  const mainHeaderId = useId()
  const [scrollMargin, setScrollMargin] = useState(0)

  useLayoutEffect(() => {
    const rowsContainer = rowsContainerRef.current
    if (!scrollElement || !rowsContainer) return
    setScrollMargin(
      rowsContainer.getBoundingClientRect().top -
        scrollElement.getBoundingClientRect().top +
        scrollElement.scrollTop,
    )
  }, [scrollElement, showRecents, recentPbxs.length, showRows])

  const virtualizer = useVirtualizer({
    count: mainPbxs.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
    scrollMargin,
  })

  // Fetch next page when scrolling near the bottom.
  // We avoid putting getVirtualItems() in the dep array because it returns
  // a new reference every call. Instead we depend on the virtualizer's
  // internal range and trigger on scroll via the virtualizer's own reactivity.
  const virtualItems = virtualizer.getVirtualItems()
  const lastVirtualItemIndex = virtualItems.length
    ? virtualItems[virtualItems.length - 1].index
    : -1

  useEffect(() => {
    if (
      lastVirtualItemIndex >= mainPbxs.length - 10 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      onLoadMore()
    }
  }, [
    lastVirtualItemIndex,
    mainPbxs.length,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
  ])

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedOptionId == null) return
    const idx = mainPbxs.findIndex(
      (p) => getOptionId(OptionSection.Main, p.id) === highlightedOptionId,
    )
    if (idx >= 0) {
      virtualizer.scrollToIndex(idx, { align: 'auto' })
    }
  }, [highlightedOptionId, getOptionId, mainPbxs, virtualizer])

  return (
    <>
      {/* Recents (non-virtualized, always small) */}
      {showRecents && (
        <>
          <Flex
            direction="column"
            gap="0.5"
            role="group"
            aria-labelledby={recentHeaderId}
          >
            <SectionHeader id={recentHeaderId} label={recentLabel} />
            {recentPbxs.map((pbx) => {
              const optionId = getOptionId(OptionSection.Recent, pbx.id)
              return (
                <PbxListItem
                  key={`recent-${pbx.id}`}
                  pbx={pbx}
                  optionId={optionId}
                  isCurrent={pbx.id === currentPbxId}
                  isHighlighted={optionId === highlightedOptionId}
                  onSelect={() => onSelect(pbx)}
                  scrollElement={scrollElement}
                />
              )
            })}
          </Flex>
          <Separator my="1" borderColor="border.emphasized" aria-hidden />
        </>
      )}

      {/* Main virtualized list */}
      <Flex
        direction="column"
        gap="0.5"
        role="group"
        aria-labelledby={mainHeaderId}
      >
        <SectionHeader id={mainHeaderId} label={allPbxsLabel} />
        {searchLoading ? (
          <Flex align="center" justify="center" py="4">
            <Spinner size="sm" color="fg.subtle" />
          </Flex>
        ) : mainPbxs.length === 0 ? (
          <Text
            fontSize="sm"
            color="fg.subtle"
            px="3"
            py="4"
            textAlign="center"
          >
            {noResultsText}
          </Text>
        ) : (
          <div
            ref={rowsContainerRef}
            role="presentation"
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {virtualItems.map((virtualRow) => {
              const pbx = mainPbxs[virtualRow.index]
              const optionId = getOptionId(OptionSection.Main, pbx.id)
              return (
                <div
                  key={pbx.id}
                  role="presentation"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
                  }}
                >
                  <PbxListItem
                    pbx={pbx}
                    optionId={optionId}
                    isCurrent={pbx.id === currentPbxId}
                    isHighlighted={optionId === highlightedOptionId}
                    onSelect={() => onSelect(pbx)}
                  />
                </div>
              )
            })}
          </div>
        )}
      </Flex>

      {isFetchingNextPage && (
        <Flex align="center" justify="center" py="2">
          <Spinner size="sm" color="fg.subtle" />
        </Flex>
      )}
    </>
  )
}
